import AnimationPlayer from "../AnimationPlayer/AnimationPlayer";
import { ImageType, isAvailable } from "../../types/baseTypes";
import styles from "../../modules/Media/Media.module.scss";
import { customSanitize } from "../../utils/customSanitize";
import { MediaFormatType } from "@/types/ModuleOptionsType";
import { MediaModuleType } from "../../modules/Media/MediaModul";
import { TextMediaBigModuleType } from "../../modules/TextMediaBig/TextMediaBig";

import { MediaBackgroundModuleType } from "../../modules/MediaBackground/MediaBackground";
import { useEffect, useState } from "react";
import { getVideoData } from "@/getData/getVideoData";

import { ContentBlockerWrapper } from "@aleksundshantu/aunds-cookie-plugin-react";
import { Post } from "@/wp/types/client";
import Image from "next/image";
import VidstackAudioPlayer from "../VidstackPlayer/VidstackAudioPlayer";
import { getAudioData } from "@/getData/getAudioData";
import { TextMediaModuleType } from "@/modules/TextMedia/TextMedia";
import { MediaItemResponsive } from "../MediaItem/MediaItemResponsive";
import { InternalVideo, Video } from "../Video/Video";
import VidstackPlayer from "../VidstackPlayer/VidstackPlayer";
import dynamic from "next/dynamic";
import { defaultLineChartOptions } from "../DiagramChart/DiagramChart.options";
import { DiagramData } from "@/getData/getTextMediaDiagramData";
import { FileType } from "../../types/baseTypes";
import { generateMediaFormat } from "@/utils/layout/mediaFormat";
import { useRouter } from "next/router";

const DiagramChart = dynamic(
  () =>
    import("../DiagramChart/DiagramChart").then((m) => ({
      default: m.DiagramChart,
    })),
  { ssr: false },
);

interface MediaContentProps {
  mediaData:
    | MediaModuleType["media"]["media"]
    | TextMediaBigModuleType["textmedia_big"]["media"]
    | TextMediaModuleType["textmedia"]["media"]
    | MediaBackgroundModuleType["mediabackground"]["media"];
  sizes?: string;
  mediaFormat?: MediaFormatType;
  showAudioIcon?: boolean;
  sectionBgColor?: string;
  metalCsvFile?: FileType;
  diagramData?: DiagramData;
}

const Media = ({
  mediaData,
  sizes = "(max-width: 767px) 50vw, (max-width: 1023px) 100vw, 768px",
  mediaFormat,
  sectionBgColor = "",
  metalCsvFile,
  diagramData,
}: MediaContentProps) => {
  const videoId = "video" in mediaData ? mediaData.video?.[0] : undefined;
  const audioId = "audio" in mediaData ? mediaData.audio?.[0] : undefined;
  const router = useRouter();
  const [videoData, setVideoData] = useState<Post<"videos"> | null>(null);
  const [audioData, setAudioData] = useState<Post<"audios"> | null>(null);

  //Content Blocker Poster
  const [imageURL, setImageURL] = useState("");
  const [videoPlaying, setVideoPlaying] = useState(false);

  const locale = router?.locale;

  const autoplay = "autoplay" in mediaData ? mediaData.autoplay : false;
  const video_decorative =
    "video_decorative" in mediaData ? mediaData.video_decorative : false;

  const { show_caption, caption, caption_position } = mediaData;
  const type = "type" in mediaData ? mediaData.type : "image";

  const imgfhd = isAvailable<ImageType>(mediaData.image_fullhd)
    ? mediaData.image_fullhd
    : false;
  const imgdsk = isAvailable<ImageType>(mediaData.image_desktop)
    ? mediaData.image_desktop
    : false;
  const imgtab = isAvailable<ImageType>(mediaData.image_tablet)
    ? mediaData.image_tablet
    : false;
  const imgmob = isAvailable<ImageType>(mediaData.image_mobile)
    ? mediaData.image_mobile
    : false;

  const svgAnimation =
    "svg_animation" in mediaData ? mediaData.svg_animation : undefined;

  const headerVideos = videoData?.acf?.header_videos;
  const videoType = headerVideos?.video_type;

  const shouldUseContentBlocker =
    type === "video" &&
    !!videoData &&
    (videoType === "Youtube" ||
      videoType === "Vimeo" ||
      videoType === "iframe");
  let mediaJSX, video;

  useEffect(() => {
    if (!shouldUseContentBlocker) return;

    setImageURL("");
    setVideoPlaying(false);

    const html =
      videoData?.acf?.header_videos?.externes_video ||
      videoData?.acf?.header_videos?.externes_video_iframe;
    const src = html?.match(/src="([^"]+)"/)?.[1];
    if (!src) return;

    (async () => {
      try {
        const res = await fetch(
          `/api/fetchVideoThumbnail?url=${encodeURIComponent(src)}`,
        );
        if (!res.ok)
          throw new Error(
            "Error fetchining Video Thumbnail: Network response was not ok",
          );
        const data = await res.json();
        const thumb =
          data?.videoThumbnail?.dataURL || data?.videoThumbnail?.url;
        if (thumb) setImageURL(thumb);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [shouldUseContentBlocker, videoData]);

  useEffect(() => {
    async function fetchVideoData() {
      if (!videoId || type !== "video") return;
      const videoResponse = await getVideoData({
        videoId: videoId as string,
        fields: "acf.header_videos,link",
        locale: router.locale,
        defaultLocale: router.defaultLocale,
      });
      if (videoResponse) {
        setVideoData(videoResponse);
      }
    }
    async function fetchAudioData() {
      if (!audioId || type !== "audio") return;

      const audioResponse = await getAudioData({
        audioId: audioId as string,
        fields: "acf.header_audios,link",
        locale: router.locale,
        defaultLocale: router.defaultLocale,
      });

      if (audioResponse) {
        setAudioData(audioResponse);
      }
    }

    fetchVideoData();
    fetchAudioData();
  }, [type, videoId, audioId, router.locale, router.defaultLocale]);

  switch (type) {
    case "image":
      mediaJSX = (
        <MediaItemResponsive
          image_fullhd={imgfhd}
          image_desktop={imgdsk}
          image_tablet={imgtab}
          image_mobile={imgmob}
          sizes={sizes}
          mediaFormat={mediaFormat}
          figcaption={show_caption && caption ? caption : ""}
        />
      );
      break;
    case "video":
      if (!videoId || !videoData) {
        mediaJSX = null;
        break;
      }
      const {
        externes_video_iframe,
        externes_video,
        internes_video_desktop,
        video_type,
        audio,
        has_single_page,
      } = videoData.acf.header_videos;
      switch (video_type) {
        case "iframe":
          video = externes_video_iframe;
          mediaJSX = video ? <Video onlineVideo={video} /> : null;
          break;
        case "Vimeo":
        case "Youtube":
          video = externes_video;
          mediaJSX = video ? (
            <>
              <VidstackPlayer
                video={videoData.acf.header_videos}
                autoplay={autoplay || false}
                muted={!audio}
                singlePageUrl={has_single_page ? videoData.link : ""}
                sizes="(min-width: 1215px) 100vw, (min-width: 1408px) 75vw, 1024px"
              />
            </>
          ) : null;
          break;
        case "upload":
          mediaJSX = internes_video_desktop ? (
            <InternalVideo
              video={videoData.acf.header_videos}
              autoplay={autoplay || false}
              muted={!audio}
              video_decorative={video_decorative}
              singlePageUrl={has_single_page ? videoData.link : ""}
            />
          ) : null;
          break;
        default:
          mediaJSX = null;
          break;
      }
      break;
    case "animation":
      mediaJSX =
        svgAnimation && svgAnimation.url ? (
          <AnimationPlayer url={svgAnimation.url} autoplay={true} />
        ) : null;
      break;
    case "audio":
      const headerAudios = audioData?.acf?.header_audios;

      mediaJSX = headerAudios ? (
        <VidstackAudioPlayer
          audio={headerAudios}
          loop={false}
          singlePageUrl={headerAudios.has_single_page ? audioData.link : ""}
          sectionBgColor={sectionBgColor}
        />
      ) : null;
      break;
    case "diagram":
      if (metalCsvFile && diagramData?.name) {
        const diagramOptions = {
          ...defaultLineChartOptions,
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            ...defaultLineChartOptions.scales,
            y: {
              ...defaultLineChartOptions.scales?.y,
              min: diagramData.min ?? undefined,
              max: diagramData.max ?? undefined,
            },
          },
        };
        mediaJSX = (
          <div
            className={`p-5 is-box has-border-radius is-relative${mediaFormat ? ` ${generateMediaFormat(mediaFormat)}` : ""}`}
          >
            <DiagramChart
              url={metalCsvFile.url}
              className="is-centered-row"
              options={diagramOptions}
              name={diagramData.name}
              min={diagramData.min ?? undefined}
              max={diagramData.max ?? undefined}
            />
          </div>
        );
      } else {
        mediaJSX = (
          <p>
            Es wurden keine Daten übergeben, um einen Graphen zu generieren.
          </p>
        );
      }
      break;
  }
  return (
    <div className="has-border-radius is-clipped">
      {shouldUseContentBlocker ? (
        <div
          className={`${styles["content-blocker-video-wrapper"]} is-relative ${videoPlaying ? styles["is-playing"] : ""}`}
          onClickCapture={(e) => {
            const el = e.target as HTMLElement | null;
            if (el?.closest?.(".content-blocker-button")) setVideoPlaying(true);
          }}
        >
          {!!imageURL && (
            <Image
              className={styles["video-thumbnail"]}
              src={imageURL}
              alt=""
              fill
              unoptimized
              priority
            />
          )}

          <ContentBlockerWrapper
            platformName={videoType || "unknown"}
            wpApiUrl={process.env.WP_API_URL!}
            extraClassName={{ wrapper: styles["video-blocker"] }}
            locale={
              locale !== process.env.NEXT_PUBLIC_DEFAULT_LOCALE
                ? locale
                : undefined
            }
          >
            {mediaJSX}
          </ContentBlockerWrapper>
        </div>
      ) : (
        mediaJSX
      )}

      {type !== "image" && show_caption && caption && (
        <figcaption
          dangerouslySetInnerHTML={{ __html: customSanitize(caption) }}
          className={`caption ${caption_position}`}
        />
      )}
    </div>
  );
};

export default Media;
